import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import React, { useContext, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { SectionServices } from './SectionServices';
import Loading from '../../../../components/Loader/Loading';
import { SectionContext } from './Sections';

const GROUPS = ['header', 'section', 'footer'];

const SectionReorder = () => {
    const { list, fetchSectionList } = useContext(SectionContext);
    const [groups, setGroups] = useState({ header: [], section: [], footer: [] });
    const [originalGroups, setOriginalGroups] = useState({ header: [], section: [], footer: [] });
    const [dragged, setDragged] = useState({ index: null, group: null });
    const [dragOver, setDragOver] = useState({ index: null, group: null });
    const [loading, setLoading] = useState(true);

    const getPageType = (item) => item?.displayOptions?.pageType || 'section';

    const groupSections = (sections) => {
        const grouped = { header: [], section: [], footer: [] };
        sections.forEach(item => {
            const type = getPageType(item);
            if (grouped[type]) grouped[type].push(item);
        });
        GROUPS.forEach(g => grouped[g].sort((a, b) => a.position - b.position));
        return grouped;
    };

    const flattenWithPositions = (grouped) => {
        const result = [];
        GROUPS.forEach(g => {
            grouped[g].forEach((item, i) => {
                result.push({ id: item.id, position: i + 1, _group: g });
            });
        });
        return result;
    };

    useEffect(() => {
        if (list && list.length > 0) {
            const active = list.filter(item => item.isActive);
            const grouped = groupSections(active);
            setGroups(grouped);
            setOriginalGroups({ header: [...grouped.header], section: [...grouped.section], footer: [...grouped.footer] });
            setLoading(false);
        } else {
            fetchAllSections();
        }
    }, [list]);

    const fetchAllSections = async () => {
        try {
            setLoading(true);
            const res = await SectionServices.fetchSectionList();
            if (res.success == '1') {
                const active = (res.data || []).filter(item => item.isActive);
                const grouped = groupSections(active);
                setGroups(grouped);
                setOriginalGroups({ header: [...grouped.header], section: [...grouped.section], footer: [...grouped.footer] });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const autoSave = async (updatedSections) => {
        try {
            setLoading(true);
            const response = await SectionServices.reorderSections(updatedSections);
            if (response.success == '1') {
                setOriginalGroups({ header: [...groups.header], section: [...groups.section], footer: [...groups.footer] });
                if (typeof fetchSectionList === 'function') {
                    await fetchSectionList();
                }
            } else {
                handleCancel();
                Swal.fire({ icon: 'error', title: 'Save Failed', text: response.message || 'Failed to update section positions', timer: 2000 });
            }
        } catch (error) {
            handleCancel();
            Swal.fire({ icon: 'error', title: 'Save Failed', text: 'An error occurred while updating section positions', timer: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (e, group, index) => {
        if (group !== 'section') {
            e.preventDefault();
            return;
        }
        setDragged({ index, group });
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, group, index) => {
        if (!dragged.group || dragged.group !== group) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver({ index, group });
    };

    const handleDragLeave = () => setDragOver({ index: null, group: null });

    const handleDrop = async (e, dropGroup, dropIndex) => {
        e.preventDefault();
        if (dragged.index === null || (dragged.group === dropGroup && dragged.index === dropIndex)) {
            setDragged({ index: null, group: null });
            setDragOver({ index: null, group: null });
            return;
        }
        if (dragged.group !== dropGroup) {
            setDragged({ index: null, group: null });
            setDragOver({ index: null, group: null });
            return;
        }

        const newGroups = { ...groups };
        const list = [...newGroups[dragged.group]];
        const [item] = list.splice(dragged.index, 1);
        list.splice(dropIndex, 0, item);
        newGroups[dragged.group] = list;

        setGroups(newGroups);

        const updatedSections = flattenWithPositions(newGroups);
        await autoSave(updatedSections);

        setDragged({ index: null, group: null });
        setDragOver({ index: null, group: null });
    };

    const handleDragEnd = () => setDragged({ index: null, group: null });

    const handleCancel = () => {
        setGroups({ header: [...originalGroups.header], section: [...originalGroups.section], footer: [...originalGroups.footer] });
    };

    const groupLabel = (g) => g === 'header' ? 'Header' : g === 'footer' ? 'Footer' : 'Section';

    return (
        <Box component="div">
            {loading && <Loading />}
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, color: '#3e3e3e' }}>
                Reorder Sections
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#666', mb: 1 }}>
                Drag and drop to reorder active sections
            </Typography>
            <Grid container spacing={1} sx={{ maxHeight: { xs: '300px', lg: 'calc(100vh - 250px)' }, overflow: 'auto' }}>
                {GROUPS.map(group => (
                    <React.Fragment key={group}>
                        {(groups[group]?.length > 0 || !loading) && (
                            <Grid item xs={12}>
                                <Typography sx={{
                                    fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '0.5px', color: '#888', mt: 1, mb: 0.5, px: 0.5
                                }}>
                                    {groupLabel(group)}
                                </Typography>
                            </Grid>
                        )}
                        {groups[group]?.length > 0 ? groups[group].map((item, index) => {
                            const isLocked = group !== 'section';
                            const isOver = dragOver.group === group && dragOver.index === index && dragged.index !== index;
                            return (
                                <Grid
                                    item xs={12} key={item.id}
                                    draggable={!isLocked}
                                    onDragStart={(e) => handleDragStart(e, group, index)}
                                    onDragOver={(e) => handleDragOver(e, group, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, group, index)}
                                    onDragEnd={handleDragEnd}
                                    sx={{
                                        opacity: dragged.group === group && dragged.index === index ? 0.5 : 1,
                                        cursor: isLocked ? 'default' : 'grab',
                                        '&:active': { cursor: isLocked ? 'default' : 'grabbing' },
                                        position: 'relative',
                                        '&::before': isOver ? {
                                            content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                            border: '2px dashed #2196f3', borderRadius: '4px', zIndex: 1, pointerEvents: 'none'
                                        } : {}
                                    }}
                                >
                                    <Card sx={{
                                        position: 'relative', mb: 1,
                                        '&:hover': { boxShadow: 3, transform: 'translateY(-2px)', transition: 'all 0.2s ease-in-out' },
                                        backgroundImage: item.backgroundImage ? `url(${item.backgroundImage})` : 'none',
                                        backgroundSize: 'cover', backgroundPosition: 'center',
                                        backgroundColor: item.backgroundColor || '#fff',
                                    }}>
                                        <CardContent sx={{ p: '8px !important' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{
                                                    backgroundColor: 'primary.main', color: 'white', borderRadius: '50%',
                                                    width: 28, height: 28, display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold'
                                                }}>
                                                    {index + 1}
                                                </Box>
                                                <Box sx={{ bgcolor: '#fff', p: 1, borderRadius: 1, overflow: 'hidden' }}>
                                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }} noWrap>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.6rem', color: '#666' }}>
                                                        {item.sectionType?.replace(/_/g, ' ')}
                                                    </Typography>
                                                </Box>
                                                <DragIndicator sx={{ color: isLocked ? '#bbb' : '#666', fontSize: '18px', cursor: isLocked ? 'default' : 'grab' }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        }) : !loading && (
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: '0.7rem', color: '#999', textAlign: 'center', py: 0.5 }}>
                                    No {groupLabel(group).toLowerCase()} sections
                                </Typography>
                            </Grid>
                        )}
                    </React.Fragment>
                ))}
                {!loading && GROUPS.every(g => !groups[g]?.length) && (
                    <Grid item xs={12}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', textAlign: 'center', py: 2 }}>
                            No active sections to reorder
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default SectionReorder;
